import { useTranslation } from 'react-i18next';
import type { SampleRequest } from '@/mocks/dashboard';

interface Props {
  samples: SampleRequest[];
}

const statusColors: Record<string, string> = {
  requested: 'bg-ink-50 text-ink-400 border-ink-200',
  under_review: 'bg-warm-50 text-warm-700 border-warm-200',
  quoted: 'bg-ink-50 text-ink-600 border-ink-200',
  in_production: 'bg-warm-50 text-warm-700 border-warm-200',
  shipped: 'bg-warm-50 text-warm-700 border-warm-200',
  completed: 'bg-ink-50 text-ink-600 border-ink-200',
  cancelled: 'bg-warm-50 text-warm-800 border-warm-300',
};

export default function DashboardSamples({ samples }: Props) {
  const { t } = useTranslation('dashboard');

  return (
    <div className="rounded-xl border border-ink-100 bg-white">
      <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border-b border-ink-100">
        <div className="flex items-center gap-2">
          <i className="ri-test-tube-line text-warm-400 text-lg" />
          <h3 className="text-sm md:text-base font-semibold text-ink-600">{t('dashboard.samples.title')}</h3>
        </div>
        <a href="/my/samples" className="text-xs md:text-sm font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap">
          {t('dashboard.seeAll')}
        </a>
      </div>

      {samples.length === 0 ? (
        <div className="px-4 md:px-5 py-8 text-center">
          <div className="w-12 h-12 rounded-full bg-cream-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-test-tube-line text-cream-400 text-xl" />
          </div>
          <p className="text-sm text-ink-300">{t('dashboard.samples.empty')}</p>
        </div>
      ) : (
        <div className="divide-y divide-ink-100">
          {samples.map((s) => (
            <div key={s.id} className="px-4 md:px-5 py-3 md:py-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono text-warm-400">{s.requestNumber}</span>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${statusColors[s.status]}`}>
                    {t(`dashboard.sampleStatus.${s.status}`)}
                  </span>
                </div>
                <p className="text-sm font-medium text-ink-600 mt-1">{s.productName}</p>
                <p className="text-xs text-ink-300 mt-0.5">{s.finishSummary}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-ink-300">
                  <span>{t('dashboard.samples.quantity')}: {s.quantity}</span>
                  <span>{new Date(s.createdAt).toLocaleDateString()}</span>
                </div>
                {s.estimatedCost !== null && (
                  <p className="text-xs font-medium text-ink-500 mt-1">
                    {t('dashboard.samples.cost')}: {s.currency} {s.estimatedCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                )}
              </div>
              <a
                href={`/my/samples/${s.id}`}
                className="shrink-0 text-xs font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap mt-0.5"
              >
                {t('dashboard.samples.view')}
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}